import type {
  DicomPullRequest,
  ImageRetrivalInfo,
  ImageUIDLookupTable,
  MetaData,
  PayloadImageData,
} from '@src/components/backend/payload-typing';

import { orthancJwt } from '@src/core/useOrthancAuth';
import { useViewportStateStore } from '@src/store/contours/viewport-state-store';
import { Orientation } from '@src/store/dicom-web/classification-typing';
import { useCurrentStudyStore } from '@src/store/dicom-web/current-study-store';
import { useOrthancStore } from '@src/store/dicom-web/orthanc-store';
import { seriesClassification } from '@src/store/dicom-web/SeriesClassification';

async function getDicomArchiveConfig() {
  const dicomArchiveStore = useOrthancStore();
  const dicomArchiveConfig = await dicomArchiveStore.getConfig();
  return dicomArchiveConfig;
}

/**
 * Similar to getAllImageUIDs, but uses the imageUIDs directly to populate the DicomPullRequest objects.
 *
 */
export async function getPayloadFromImageUIDs(imageUIDs: string[]): Promise<{
  dicomPullRequestObject: DicomPullRequest[];
  metaData: MetaData;
}> {
  const currentStudyStore = useCurrentStudyStore();

  const studyID = currentStudyStore.getStudyID;
  const dicomPullRequestObject: DicomPullRequest[] = [];
  const dicomArchiveConfig = await getDicomArchiveConfig();

  const metaData: MetaData = {
    orientationBySeries: {},
    classificationBySeries: {},
    orthancServerBaseURL: dicomArchiveConfig.url,
    orthancJwt: await orthancJwt(dicomArchiveConfig.id),
    selectedSeries: [],
  };

  for (const sopInstanceUID of imageUIDs) {
    const seriesUID = currentStudyStore.getSeriesUIDByImageUID(sopInstanceUID);

    if (!seriesUID) {
      console.warn(
        `No series UID found for SOP Instance UID: ${sopInstanceUID}`,
      );
      continue;
    }

    if (!metaData.classificationBySeries[seriesUID]) {
      const imageInfo = currentStudyStore.getIndexByImageUID(sopInstanceUID);

      if (imageInfo && imageInfo.imageSeriesIndex) {
        const classification
          = seriesClassification.getClassificationBySopInstanceUID(
            sopInstanceUID,
          );
        if (
          classification
          && classification.orientation
          && classification.orientation !== Orientation.Undefined
        ) {
          // Store the classification orientation
          metaData.classificationBySeries[seriesUID]
            = classification.orientation;
        }
        else {
          // If no classification is available, store SAX as default
          metaData.classificationBySeries[seriesUID] = Orientation.Sax;
        }
      }
    }

    dicomPullRequestObject.push({
      StudyInstanceUID: studyID,
      SeriesInstanceUID: seriesUID,
      SOPInstanceUID: sopInstanceUID,
    });
  }

  return { dicomPullRequestObject, metaData };
}

/**
 * Get all image UIDs based on viewer info
 */
export async function getAllImageUIDs(imageInfo: ImageRetrivalInfo): Promise<{
  dicomPullRequestObject: DicomPullRequest[];
  metaData: MetaData;
  imageUIDLookupTable: ImageUIDLookupTable; // Optional for cases where image lookup is not required
}> {
  const currentStudyStore = useCurrentStudyStore();
  const viewportStateStore = useViewportStateStore();
  const completedUID: string[] = []; // Store completed UIDs to avoid duplicates
  const { viewerIDs, include, exclude, requiresImageLookup } = imageInfo;
  const studyID = currentStudyStore.getStudyID;
  const dicomPullRequestObject: DicomPullRequest[] = [];
  const imageUIDLookupTable: ImageUIDLookupTable = {};

  const dicomArchiveConfig = await getDicomArchiveConfig();

  const metaData: MetaData = {
    orientationBySeries: {},
    classificationBySeries: {},
    orthancServerBaseURL: dicomArchiveConfig.url,
    orthancJwt: await orthancJwt(dicomArchiveConfig.id),
    selectedSeries: [],
  };
  let runningSliceIndex = -1;

  viewerIDs.forEach((viewerID) => {
    runningSliceIndex += 1;
    const imageIndices = viewportStateStore.getSelectedImageIndices(viewerID);
    const seriesIndex = imageIndices.seriesIdx;
    const seriesUID = currentStudyStore.getSeriesUID(seriesIndex);

    if (!seriesUID) {
      throw new Error(`No series UID found for viewer ${viewerID}`);
    }

    const imageUIDsfromStore
      = currentStudyStore.getSliceImageUIDsbyView(viewerID);

    const indices = {
      seriesIdx: seriesIndex,
      sliceIdx: imageIndices.sliceIdx,
      imageIdx: 0,
    };
    metaData.selectedSeries.push({
      seriesUID,
      seriesIdx: seriesIndex,
      sliceIndex: indices.sliceIdx,
      analysisSliceIndex: runningSliceIndex, // The index of the slice in the order it will be analyzed (viewer order)
    });

    const { orientation } = currentStudyStore.getVtkMetaDataIndex(indices);
    // Check if the seriesUID is already classified
    if (!metaData.classificationBySeries[seriesUID]) {
      const classification
        = seriesClassification.getClassificationByViewport(viewerID);
      if (
        classification
        && classification.orientation
        && classification.orientation !== Orientation.Undefined
      ) {
        // Store the classification orientation
        metaData.classificationBySeries[seriesUID] = classification.orientation;
      }
      else {
        // If no classification is available, store SAX as default
        metaData.classificationBySeries[seriesUID] = Orientation.Sax;
      }
    }
    metaData.orientationBySeries[seriesUID] = Array.from(orientation);
    let correctImageIndex = 0;
    for (let i = 0; i < imageUIDsfromStore.length; i++) {
      const sopInstanceUID = imageUIDsfromStore[i];
      const originalSeriesUID
        = currentStudyStore.getSeriesUIDByImageUID(sopInstanceUID);
      if (!originalSeriesUID) {
        console.warn(
          `No series UID found for SOP Instance UID: ${sopInstanceUID}`,
        );
        continue;
      }
      // Check if the imageUID is included or excluded
      if (completedUID.includes(sopInstanceUID)) continue;
      if (include && !include.includes(sopInstanceUID)) continue;

      if (requiresImageLookup) {
        imageUIDLookupTable[sopInstanceUID] = {
          studyUID: studyID,
          seriesUID: originalSeriesUID,
          imageUID: sopInstanceUID,
          sliceIndex: indices.sliceIdx,
          imageIndex: correctImageIndex,
          realImageIndex: i,
        };
        correctImageIndex += 1;
      }
      completedUID.push(sopInstanceUID);

      if (exclude && exclude.includes(sopInstanceUID)) continue;

      dicomPullRequestObject.push({
        StudyInstanceUID: studyID,
        SeriesInstanceUID: originalSeriesUID,
        SOPInstanceUID: sopInstanceUID,
      });
    }
  });

  return { dicomPullRequestObject, imageUIDLookupTable, metaData };
}

/**
 * Attach image data to DICOM pull request objects
 */
export async function getAllAsImages(
  dicomPullRequestObject: DicomPullRequest[],
): Promise<DicomPullRequest[]> {
  const dicomPullObjectCopy = JSON.parse(
    JSON.stringify(dicomPullRequestObject),
  ) as DicomPullRequest[];

  const currentStudyStore = useCurrentStudyStore();

  for (let i = 0; i < dicomPullObjectCopy.length; i++) {
    let finalImage: PayloadImageData = [];
    const { SOPInstanceUID } = dicomPullObjectCopy[i];

    const imageData
      = await currentStudyStore.fetchOrGetVtkImageData(SOPInstanceUID);

    if (!imageData) {
      console.error('Unable to retrieve image at', SOPInstanceUID);
      continue;
    }

    const targetImage = imageData.getPointData().getScalars().getData();
    const dimensions = imageData.getDimensions();
    finalImage = targetImage;

    dicomPullObjectCopy[i].imageData = finalImage;
    dicomPullObjectCopy[i].dimensions = dimensions;
  }

  return dicomPullObjectCopy;
}
